import {
  ICorrespondentSearchInputModel,
  IGetHistoryInputModel,
  IGetTotalSolicitationsInputModel,
  IRequestsSubmittedInputModel,
  IUpdateSolicitationValueInputModel,
  RefuseConclusionInputModel,
  RefuseProposalInputModel,
  SendCounterProposalInputModel,
  ICancelDemandInputModel,
} from 'inputModels';
import {
  ICollaboratorInfoGetSolicitation,
  IFilter,
  IFinancialReportFilter,
  IReportFilter,
} from 'props';
import qs from 'qs';
import {
  ICorrespondentResultViewModel,
  IDemandViewModel,
  IFinancialReportViewModel,
  IHistoryViewModel,
  IDemandProposalViewModal,
  ISolicitationViewModel,
  ILocationViewModel,
  ISolicitationDuplicatedViewModel,
  IProviedServicesViewModel,
  IDashboardSolicitationStatsViewModel,
} from 'viewModels';
import api from '../services/api/axios';

const insert = (input: FormData) =>
  new Promise<string>((resolve, reject) => {
    api
      .post(`solicitation`, input, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const update = (id: string, input: FormData) =>
  new Promise<void>((resolve, reject) => {
    api
      .put(`solicitation/${id}`, input, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(() => resolve())
      .catch(() => reject());
  });

const compare = (solicitationId: string) =>
  new Promise<ISolicitationViewModel | undefined>((resolve, reject) => {
    api
      .post(`solicitation/compare/${solicitationId}`)
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const compareBeforeInsert = (input: FormData) =>
  new Promise<ISolicitationDuplicatedViewModel | undefined>((resolve, reject) => {
    api
      .post(`solicitation/compare-before-insert`, input, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const get = (input?: IFilter) => {
  return new Promise<ISolicitationViewModel[]>((resolve, reject) => {
    api
      .get(`solicitation`, {
        params: input,
        paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
      })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
};

const getByCollaborator = (
  input?: IFilter,
  infosCollaborator?: ICollaboratorInfoGetSolicitation,
) => {
  return new Promise<ISolicitationViewModel[]>((resolve, reject) => {
    api
      .post(`solicitation/get-by-collaborator`, infosCollaborator, {
        params: input,
        paramsSerializer: (params: IFilter) => qs.stringify(params),
      })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
};

const getFinancialReport = (input: IFinancialReportFilter) => {
  return new Promise<IFinancialReportViewModel[]>((resolve, reject) => {
    api
      .get(
        `solicitation/financial-report?${input?.orderBy
          ?.map((o, i) => `orderBy[${i}].key=${o.key}&orderBy[${i}].direction=${o.direction}`)
          .join('&')}`,

        {
          params: input,
          paramsSerializer: (params) => qs.stringify(params),
        },
      )
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
};

const getById = (id: string) =>
  new Promise<ISolicitationViewModel>((resolve, reject) => {
    api
      .get(`solicitation/${id}`)
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const getDemandProposalById = (id: string) =>
  new Promise<ISolicitationViewModel>((resolve, reject) => {
    api
      .get(`solicitation/demand-proposal/${id}`)
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const getDemandById = (id: string) =>
  new Promise<ISolicitationViewModel>((resolve, reject) => {
    api
      .get(`solicitation/demand/${id}`)
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const getTotalSolicitations = (input: IGetTotalSolicitationsInputModel) =>
  new Promise<number>((resolve, reject) => {
    api
      .get(`solicitation/get-total-solicitations`, {
        params: input,
        paramsSerializer: (params) => qs.stringify(params),
      })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
const updateStatus = (input: FormData) => {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`solicitation/update-status`, input, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(() => resolve())
      .catch(() => reject());
  });
};
const updateSolicitationValue = (input: IUpdateSolicitationValueInputModel) => {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`solicitation/update-solicitation-value`, input)
      .then(() => resolve())
      .catch(() => reject());
  });
};
const getCorrespondents = (params: ICorrespondentSearchInputModel) =>
  new Promise<ICorrespondentResultViewModel[]>((resolve, reject) => {
    api
      .get(`solicitation/find-correspondents`, { params })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const getPaymentCorrespondents = (userId: string, page: number, take: number) =>
  new Promise<ICorrespondentResultViewModel[]>((resolve, reject) => {
    api
      .get(`solicitation/${userId}/get-payment-correspondents`, { params: { page, take } })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const sendProposals = (solicitationId: string, receivers: string[]) =>
  new Promise<void>((resolve, reject) => {
    api
      .post(`solicitation/${solicitationId}/send-proposals`, receivers)
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const updateStatusProposals = (input: IRequestsSubmittedInputModel) =>
  new Promise<void>((resolve, reject) => {
    api
      .put(`solicitation/update-status-proposal`, input)
      .then(() => resolve())
      .catch(() => reject());
  });

const undo = (solicitationId: string) =>
  new Promise<void>((resolve, reject) => {
    api
      .delete(`solicitation/undo/${solicitationId}`)
      .then(() => resolve())
      .catch(() => reject());
  });

const addHistory = (input: FormData) =>
  new Promise<string>((resolve, reject) => {
    api
      .post(`solicitation/add-history`, input, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const updateDateLimit = (id: string, newDateLimit: Date) =>
  new Promise<string>((resolve, reject) => {
    api
      .put(`solicitation/${id}/update-date-limit`, undefined, {
        params: { newDateLimit },
        headers: { 'Content-Type': 'application/json' },
      })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const updatePaymentStatus = (update: FormData) =>
  new Promise<void>((resolve, reject) => {
    api
      .put(`solicitation/update-payment-status`, update, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const deleteAttachmentSolicitationPayment = (id: string) =>
  new Promise<void>((resolve, reject) => {
    api
      .delete(`solicitation/delete-attachment-solicitation-payment/${id}`)
      .then(() => resolve())
      .catch(() => reject());
  });

const getHistory = (input: IGetHistoryInputModel) => {
  return new Promise<IHistoryViewModel[]>((resolve, reject) => {
    api
      .get(`solicitation/get-history`, { params: input })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
};

const acceptProposal = (solicitationId: string) => {
  return new Promise<IDemandViewModel>((resolve, reject) => {
    api
      .post(`solicitation/accept-proposal`, solicitationId, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
};

const refuseProposal = (input: RefuseProposalInputModel) => {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`solicitation/refuse-proposal`, input, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then(() => resolve())
      .catch(() => reject());
  });
};

const refuseConclusion = (input: RefuseConclusionInputModel) => {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`solicitation/refuse-conclusion`, input, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then(() => resolve())
      .catch(() => reject());
  });
};

const acceptCounterProposal = (requestSubmitedId: string) => {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`solicitation/${requestSubmitedId}/accept-counter-proposal`)
      .then(() => resolve())
      .catch(() => reject());
  });
};

const finalize = (id: string) => {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`solicitation/finalize/${id}`)
      .then(() => resolve())
      .catch(() => reject());
  });
};

const finalizeWithApprover = (id: string) => {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`solicitation/finalize-approver/${id}`)
      .then(() => resolve())
      .catch(() => reject());
  });
};

const getAllDemandsProposals = (input: IReportFilter) =>
  new Promise<IDemandProposalViewModal[]>((resolve, reject) => {
    api
      .get(`solicitation/get-all-proposals`, {
        params: input,
        paramsSerializer: (params) => qs.stringify(params),
      })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const sendCounterProposal = (input: SendCounterProposalInputModel) =>
  new Promise<void>((resolve, reject) => {
    api
      .post(`solicitation/send-counter-proposal`, input)
      .then(() => resolve())
      .catch(reject);
  });

const getAllDemandsByUserId = (input?: IFilter) => {
  return new Promise<IDemandViewModel[]>((resolve, reject) => {
    api
      .get(`solicitation/get-all-demands`, {
        params: input,
        paramsSerializer: (params) => qs.stringify(params),
      })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
};

const updateMultiplePaymentStatus = (update: FormData) =>
  new Promise<void>((resolve, reject) => {
    api
      .put(`solicitation/update-multiple-payment`, update, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const updateSecrecyStatus = (solicitationId: string, isSecrecy: boolean) =>
  new Promise<void>((resolve, reject) => {
    api
      .put(`solicitation/update-secrecy-status/${solicitationId}`, isSecrecy)
      .then(() => resolve())
      .catch(() => reject());
  });

const getLocation = (solicitationId: string) =>
  new Promise<ILocationViewModel>((resolve, reject) => {
    api
      .get(`solicitation/get-Location/${solicitationId}`)
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const sendLocation = (id: string, Latitude: string, Longitude: string) =>
  new Promise<void>((resolve, reject) => {
    api
      .post(`solicitation/send-Location`, {
        id,
        Latitude,
        Longitude,
      })
      .then(() => resolve())
      .catch(() => reject());
  });

const getNumberProviedServices = (uid: string) =>
  new Promise<IProviedServicesViewModel[]>((resolve, reject) => {
    api
      .get(`solicitation/get-number-provied-services/${uid}`)
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const cancelDemandByCorrespondent = (input: ICancelDemandInputModel) =>
  new Promise<void>((resolve, reject) => {
    api
      .put(`solicitation/cancel-demand-by-correspondent`, input)
      .then(() => resolve())
      .catch(() => reject());
  });
export const getDashboardSolicitationStats = (startDate: Date, endDate: Date) => {
  return new Promise<IDashboardSolicitationStatsViewModel>((resolve, reject) => {
    api
      .get('solicitation/dashboard-solicitation-stats', {
        params: {
          startDate,
          endDate,
        },
      })
      .then(({ data }) => resolve(data))
      .catch(reject);
  });
};

export const SolicitationController = {
  get,
  undo,
  insert,
  update,
  compare,
  compareBeforeInsert,
  getById,
  getDemandById,
  getDemandProposalById,
  sendProposals,
  getCorrespondents,
  getPaymentCorrespondents,
  addHistory,
  updateDateLimit,
  updatePaymentStatus,
  deleteAttachmentSolicitationPayment,
  updateStatusProposals,
  getHistory,
  acceptProposal,
  refuseProposal,
  refuseConclusion,
  finalize,
  finalizeWithApprover,
  acceptCounterProposal,
  updateStatus,
  getAllDemandsProposals,
  sendCounterProposal,
  getAllDemandsByUserId,
  updateMultiplePaymentStatus,
  updateSecrecyStatus,
  getFinancialReport,
  getByCollaborator,
  getLocation,
  sendLocation,
  getNumberProviedServices,
  getTotalSolicitations,
  updateSolicitationValue,
  cancelDemandByCorrespondent,
  getDashboardSolicitationStats,
};
